import { Alert, App, Button, Form, Input, Modal, Select, Typography } from '@wanjianshi/admin-ui';
import { useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '@/auth/store';
import { adminApi, adminOperationOptions } from '@/services/api/client';
import { loadAdminRoleOptions, type AdminRoleOption } from './roleOptions';

export function AdminRoleAssignmentModal({
  userId,
  userLabel,
  initialRoleIds,
  onSaved,
}: {
  userId: string | number;
  userLabel: string;
  initialRoleIds: unknown;
  onSaved: () => Promise<void>;
}) {
  const allowed = useAdminAuth(state => state.hasPermission('wjs-admin:admin-user:assign-role'));
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [options, setOptions] = useState<AdminRoleOption[]>([]);
  const selectedRoleIds = useMemo(
    () => Array.isArray(initialRoleIds)
      ? initialRoleIds.map(Number).filter(value => Number.isSafeInteger(value) && value > 0)
      : [],
    [initialRoleIds],
  );

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    form.setFieldsValue({ role_ids: selectedRoleIds, reason: '' });
    setLoading(true);
    void loadAdminRoleOptions(controller.signal)
      .then(setOptions)
      .catch(error => {
        if (!controller.signal.aborted) {
          message.error(error instanceof Error ? error.message : '后台角色加载失败');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [form, message, open, selectedRoleIds]);

  if (!allowed) return null;

  const submit = async () => {
    let values: { role_ids: number[]; reason: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.post(
        `/admin/admin-users/${userId}/roles`,
        { role_ids: values.role_ids },
        adminOperationOptions(values.reason.trim()),
      );
      message.success('后台角色已更新');
      setOpen(false);
      form.resetFields();
      await onSaved();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '角色分配失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Button size="small" onClick={() => setOpen(true)}>分配角色</Button>
      <Modal
        open={open}
        title={`分配后台角色 · ${userLabel}`}
        okText="保存角色"
        cancelText="取消"
        confirmLoading={submitting}
        onOk={() => void submit()}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        <Alert
          type="info"
          showIcon
          title="直接选择该管理员拥有的全部后台角色，可同时选择多个"
          description="保存后将以当前选择覆盖原后台角色，不会修改该用户在 Web 端的账户或团队权限。"
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="role_ids"
            label="后台角色"
            rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一个后台角色' }]}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={options}
              loading={loading}
              placeholder="选择一个或多个后台角色"
              maxTagCount="responsive"
              notFoundContent={loading ? '角色加载中...' : '没有可分配的后台角色'}
            />
          </Form.Item>
          <Typography.Paragraph type="secondary" style={{ marginTop: -12 }}>
            已分配角色会自动回显，取消选中后保存即可移除对应角色。
          </Typography.Paragraph>
          <Form.Item name="reason" label="操作原因" rules={[{ required: true, message: '请输入操作原因' }]}>
            <Input.TextArea rows={3} maxLength={200} showCount placeholder="说明本次角色调整原因，内容会进入操作审计" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
